/**
 * Underlying library changed: cmdk → @base-ui/react/dialog + custom Autocomplete primitives
 *
 * Command:
 *   - OLD: thin wrapper over cmdk `<Command>` (filtering, search state etc. built-in)
 *   - NEW: wraps <Autocomplete inline open> — filtering driven by Autocomplete, not cmdk
 *   - NEW props: `autoHighlight` (default 'always'), `keepHighlight` (default true)
 *   - REMOVED: `shouldFilter` — base-ui never auto-filters rendered items; pass pre-filtered items
 *
 * CommandInput:
 *   - OLD: cmdk Input; `onValueChange` callback for search query changes
 *   - NEW: AutocompleteInput; use standard `onChange` (React.ChangeEvent<HTMLInputElement>)
 *
 * CommandDialog:
 *   - OLD: combined dialog + command palette; accepted `title`, `description`, `className`
 *   - NEW: just the dialog root (= CommandDialogPrimitive.Root); no built-in title/description
 *   - NEW companions: CommandDialogTrigger, CommandDialogBackdrop, CommandDialogViewport,
 *                     CommandDialogPopup (compose these manually instead)
 *   - NEW: CommandCreateHandle (= CommandDialogPrimitive.createHandle) for imperative control
 *
 * CommandGroup:
 *   - OLD: cmdk Group (uses CSS `[cmdk-group-heading]` for label styling)
 *   - NEW: AutocompleteGroup; use CommandGroupLabel child for the heading
 *
 * CommandGroupLabel: NEW — no equivalent in old API
 * CommandCollection: NEW — no equivalent in old API (AutocompleteCollection)
 * CommandPanel:      NEW — layout wrapper div for the search panel area
 * CommandFooter:     NEW — bottom bar for hints/shortcuts
 *
 * CommandShortcut:
 *   - OLD: renders <span>
 *   - NEW: renders <kbd>
 *
 * CommandSeparator:
 *   - OLD: cmdk Separator
 *   - NEW: AutocompleteSeparator
 */
/** biome-ignore-all lint/style/useComponentExportOnlyModules: storybook export pattern */
'use client'

import { Dialog as CommandDialogPrimitive } from '@base-ui/react/dialog'
import { SearchIcon } from './icon'
import type * as React from 'react'
import { cn } from '../lib/utils'
import {
  Autocomplete,
  AutocompleteCollection,
  AutocompleteEmpty,
  AutocompleteGroup,
  AutocompleteGroupLabel,
  AutocompleteInput,
  AutocompleteItem,
  AutocompleteList,
  AutocompleteSeparator,
} from './autocomplete'

export const CommandDialog: typeof CommandDialogPrimitive.Root =
  CommandDialogPrimitive.Root

export const CommandDialogPortal: typeof CommandDialogPrimitive.Portal =
  CommandDialogPrimitive.Portal

export const CommandCreateHandle: typeof CommandDialogPrimitive.createHandle =
  CommandDialogPrimitive.createHandle

export function CommandDialogTrigger(
  props: CommandDialogPrimitive.Trigger.Props,
): React.ReactElement {
  return (
    <CommandDialogPrimitive.Trigger
      data-slot="command-dialog-trigger"
      {...props}
    />
  )
}

export function CommandDialogBackdrop({
  className,
  ...props
}: CommandDialogPrimitive.Backdrop.Props): React.ReactElement {
  return (
    <CommandDialogPrimitive.Backdrop
      className={cn('fixed inset-0 z-50 backdrop-blur-md', className)}
      data-slot="command-dialog-backdrop"
      {...props}
    />
  )
}

export function CommandDialogViewport({
  className,
  ...props
}: CommandDialogPrimitive.Viewport.Props): React.ReactElement {
  return (
    <CommandDialogPrimitive.Viewport
      className={cn(
        'fixed inset-0 z-50 grid grid-rows-[minmax(max(--spacing(4),4vh),1fr)_auto_minmax(max(--spacing(4),4vh),1fr)] items-start justify-items-center px-4',
        className,
      )}
      data-slot="command-dialog-viewport"
      {...props}
    />
  )
}

export function CommandDialogPopup({
  className,
  children,
  ...props
}: CommandDialogPrimitive.Popup.Props): React.ReactElement {
  return (
    <CommandDialogPortal>
      <CommandDialogBackdrop />
      <CommandDialogViewport>
        <CommandDialogPrimitive.Popup
          className={cn(
            'bg-surface-2 text-on-surface relative row-start-2 flex max-h-105 min-h-0 w-full max-w-xl min-w-0 -translate-y-[calc(1.25rem*var(--nested-dialogs))] scale-[calc(1-0.1*var(--nested-dialogs))] flex-col rounded-[12px] opacity-[calc(1-0.1*var(--nested-dialogs))] outline-none **:data-[slot=scroll-area-viewport]:data-has-overflow-y:pe-1',
            className,
          )}
          data-slot="command-dialog-popup"
          {...props}
        >
          {children}
        </CommandDialogPrimitive.Popup>
      </CommandDialogViewport>
    </CommandDialogPortal>
  )
}

export function Command({
  autoHighlight = 'always',
  keepHighlight = true,
  ...props
}: React.ComponentProps<typeof Autocomplete>): React.ReactElement {
  return (
    <Autocomplete
      autoHighlight={autoHighlight}
      inline
      keepHighlight={keepHighlight}
      open
      {...props}
    />
  )
}

export function CommandInput({
  className,
  containerClassName,
  placeholder,
  showSearchIcon = true,
  onKeyDown,
  ...props
}: React.ComponentProps<typeof AutocompleteInput> & {
  containerClassName?: string
  showSearchIcon?: boolean
}): React.ReactElement {
  return (
    <div
      className={cn(
        'px-1 pt-1 [&_[data-slot=autocomplete-start-addon]]:start-0 [&_[data-slot=autocomplete-start-addon]]:ps-3 [&_[data-slot=autocomplete-start-addon]_svg]:mx-0',
        containerClassName,
      )}
    >
      <AutocompleteInput
        // oxlint-disable-next-line jsx-a11y/no-autofocus -- autoFocus is intentional UX: focuses on mount for command palette search
        autoFocus
        className={cn(
          'min-h-11 rounded-md border-0 p-3 text-sm *:data-[slot=autocomplete-input]:ps-7! has-focus-visible:ring-0',
          className,
        )}
        placeholder={placeholder}
        size="lg"
        startAddon={showSearchIcon ? <SearchIcon className="size-4" /> : undefined}
        {...props}
        onKeyDown={(event) => {
          onKeyDown?.(event)
          // Inline Command has no trigger reference. Base UI otherwise handles
          // Home/End as list navigation and cancels native input selection.
          if (event.key === 'Home' || event.key === 'End') {
            event.preventBaseUIHandler()
          }
        }}
      />
    </div>
  )
}

export function CommandList({
  className,
  ...props
}: React.ComponentProps<typeof AutocompleteList>): React.ReactElement {
  return (
    <AutocompleteList
      className={cn('not-empty:scroll-py-1 not-empty:p-1', className)}
      data-slot="command-list"
      {...props}
    />
  )
}

export function CommandEmpty({
  className,
  ...props
}: React.ComponentProps<typeof AutocompleteEmpty>): React.ReactElement {
  return (
    <AutocompleteEmpty
      className={cn('not-empty:pb-6 select-none', className)}
      data-slot="command-empty"
      {...props}
    />
  )
}

export function CommandPanel({
  ...props
}: React.ComponentProps<'div'>): React.ReactElement {
  return (
    <div
      className="bg-surface-2 relative grid min-h-0 flex-1 grid-rows-[1fr] rounded-t-[12px] not-has-[+[data-slot=command-footer]]:rounded-b-[12px] **:data-[slot=scroll-area-scrollbar]:mt-1"
      {...props}
    />
  )
}

export function CommandGroup({
  ...props
}: React.ComponentProps<typeof AutocompleteGroup>): React.ReactElement {
  return <AutocompleteGroup data-slot="command-group" {...props} />
}

export function CommandGroupLabel({
  className,
  ...props
}: React.ComponentProps<typeof AutocompleteGroupLabel>): React.ReactElement {
  return (
    <AutocompleteGroupLabel
      className={cn('select-none', className)}
      data-slot="command-group-label"
      {...props}
    />
  )
}

export function CommandCollection({
  ...props
}: React.ComponentProps<typeof AutocompleteCollection>): React.ReactElement {
  return <AutocompleteCollection data-slot="command-collection" {...props} />
}

export function CommandItem({
  className,
  ...props
}: React.ComponentProps<typeof AutocompleteItem>): React.ReactElement {
  return (
    <AutocompleteItem
      className={cn(
        'cursor-pointer gap-3 rounded-md p-3 data-disabled:cursor-not-allowed!',
        className,
      )}
      data-slot="command-item"
      {...props}
    />
  )
}

export function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof AutocompleteSeparator>): React.ReactElement {
  return (
    <AutocompleteSeparator
      className={cn('my-2', className)}
      data-slot="command-separator"
      {...props}
    />
  )
}

export function CommandShortcut({
  className,
  ...props
}: React.ComponentProps<'kbd'>): React.ReactElement {
  return (
    <kbd
      className={cn(
        'text-on-surface-secondary ms-auto font-sans text-xs font-medium tracking-widest',
        className,
      )}
      data-slot="command-shortcut"
      {...props}
    />
  )
}

export function CommandFooter({
  className,
  ...props
}: React.ComponentProps<'div'>): React.ReactElement {
  return (
    <div
      className={cn(
        'text-on-surface-secondary isolate flex items-center justify-between gap-2 rounded-b-[12px] border-t px-5 py-3 text-xs',
        className,
      )}
      data-slot="command-footer"
      {...props}
    />
  )
}

export { CommandDialogPrimitive }
